import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Eye, ArrowRight, Sparkles, Gift, Target, RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const ScratchToWin = ({ isDesktop = false }) => {
  const canvasRef = useRef(null);
  const [isScratching, setIsScratching] = useState(false);
  const [scratchPercentage, setScratchPercentage] = useState(0);
  const [isRevealed, setIsRevealed] = useState(false);
  const [prize, setPrize] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const prizes = [
    { 
      id: 1, 
      title: "H&M Premium Discount", 
      subtitle: "30% OFF on Your Next Purchase", 
      image: "🎁",
      color: "from-red-500 to-red-600",
      textColor: "text-emerald-700",
      value: "30% OFF"
    },
    { 
      id: 2, 
      title: "H&M Fashion Voucher", 
      subtitle: "$50 Fashion Credit", 
      image: "💎",
      color: "from-red-500 to-red-600",
      textColor: "text-purple-700",
      value: "$50"
    },
    { 
      id: 3, 
      title: "Better Luck Next Time", 
      subtitle: "Try again tomorrow for new prizes!", 
      image: "🎯",
      color: "from-slate-400 to-slate-500",
      textColor: "text-slate-700",
      value: null
    },
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    // Create professional scratch overlay with subtle texture
    const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, '#f59e0b');    // amber-500 (darker)
    gradient.addColorStop(0.3, '#d97706');  // amber-600
    gradient.addColorStop(0.7, '#b45309');  // amber-700
    gradient.addColorStop(1, '#92400e');    // amber-800
    
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Add subtle texture pattern
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    for (let i = 0; i < rect.width; i += 4) {
      for (let j = 0; j < rect.height; j += 4) {
        if (Math.random() > 0.7) {
          ctx.fillRect(i, j, 1, 1);
        }
      }
    }
    // ✨ Add star sparkles
  for (let i = 0; i < 150; i++) {
    const x = Math.random() * rect.width;
    const y = Math.random() * rect.height;
    const radius = Math.random() * 0.6 + 0.4;

    const starGradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    starGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    starGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = starGradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
  }
    
    // Professional text styling
    ctx.fillStyle = '#fde047';
    ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.letterSpacing = '0.5px';
    ctx.fillText(' Scratch Voucher   ', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
    ctx.fillStyle = '#facc15';
    ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
    ctx.fillText('Exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
  }, [isDesktop]);

  const generateRandomPrize = () => {
    const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
    setPrize(randomPrize);
    if (randomPrize.value) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
  };

  const scratch = (e) => {
    if (!isScratching) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    let clientX, clientY;
    if (e.type.includes('touch')) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, isDesktop ? 35 : 25, 0, 2 * Math.PI);
    ctx.fill();
    
    // Calculate scratch percentage
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparent = 0;
    
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }
    
    const percentage = (transparent / (pixels.length / 4)) * 100;
    setScratchPercentage(percentage);
    
    if (percentage > 35 && !isRevealed) {
      setIsRevealed(true);
      if (!prize) generateRandomPrize();
    }
  };

  const startScratch = (e) => {
    e.preventDefault();
    setIsScratching(true);
    if (!prize) generateRandomPrize();
  };

  const endScratch = () => {
    setIsScratching(false);
  };

  const resetGame = () => {
    setIsRevealed(false);
    setPrize(null);
    setScratchPercentage(0);
    setShowCelebration(false);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    ctx.globalCompositeOperation = 'source-over';
    const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    gradient.addColorStop(0, '#f59e0b');    // amber-500 (darker)
    gradient.addColorStop(0.3, '#d97706');  // amber-600
    gradient.addColorStop(0.7, '#b45309');  // amber-700
    gradient.addColorStop(1, '#92400e');    // amber-800
    
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);
    
    // Add texture pattern
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    for (let i = 0; i < rect.width; i += 4) {
      for (let j = 0; j < rect.height; j += 4) {
        if (Math.random() > 0.7) {
          ctx.fillRect(i, j, 1, 1);
        }
      }
    }
    // ✨ Add star sparkles
  for (let i = 0; i < 150; i++) {
    const x = Math.random() * rect.width;
    const y = Math.random() * rect.height;
    const radius = Math.random() * 0.6 + 0.4;

    const starGradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    starGradient.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
    starGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.fillStyle = starGradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fill();
  }
    ctx.fillStyle = '#fde047';
    ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(' Scratch Voucher', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
    ctx.fillStyle = '#facc15';
    ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
    ctx.fillText('Exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
  };






// Replace the performDirectScratch function with this updated version:
const performDirectScratch = () => {
  if (!prize) generateRandomPrize();
  
  const canvas = canvasRef.current;
  if (!canvas) return;
  
  const ctx = canvas.getContext('2d');
  const rect = canvas.getBoundingClientRect();
  
  // Use the actual canvas dimensions, not the rect dimensions
  const canvasWidth = rect.width;
  const canvasHeight = rect.height;
  
  ctx.globalCompositeOperation = 'destination-out';
  
  // Create multiple scratch marks to simulate scratching
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;
  
  // Create a more extensive scratch pattern to ensure revelation
  const scratchRadius = isDesktop ? 40 : 35;
  
  // Create a grid of scratch marks for better coverage
  for (let x = centerX - 60; x <= centerX + 60; x += 20) {
    for (let y = centerY - 40; y <= centerY + 40; y += 20) {
      ctx.beginPath();
      ctx.arc(x, y, scratchRadius, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
  
  // Add additional circular scratch pattern
  for (let i = 0; i < 12; i++) {
    const angle = (i * Math.PI * 2) / 12;
    const radius = 40;
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    
    ctx.beginPath();
    ctx.arc(x, y, scratchRadius, 0, 2 * Math.PI);
    ctx.fill();
  }
  
  // Large center scratch
  ctx.beginPath();
  ctx.arc(centerX, centerY, scratchRadius + 10, 0, 2 * Math.PI);
  ctx.fill();
  
  // Force revelation after scratching
  setTimeout(() => {
    // Calculate scratch percentage
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    let transparent = 0;
    
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) transparent++;
    }
    
    const percentage = (transparent / (pixels.length / 4)) * 100;
    setScratchPercentage(percentage);
    
    // Force reveal if not already revealed
    if (!isRevealed) {
      setIsRevealed(true);
    }
  }, 100);
};
// Replace the existing "Start Scratching" button onClick with this:



  return (
//     <div className="w-full h-full bg-gradient-to-br from-slate-700 via-slate-400 to-slate-300 flex flex-col items-center justify-center p-4 relative overflow-hidden">
//       {/* Subtle background elements */}
//       {/* <div className="absolute inset-0 opacity-10">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-red-300 rounded-full blur-3xl"></div>
//         <div className="absolute bottom-20 right-20 w-48 h-48 bg-red-400 rounded-full blur-3xl"></div>
//       </div> */}
//       <div 
//   className="absolute inset-0 opacity-30"
//   style={{
//     backgroundImage: `url('/assets/hm1.jpg')`,
//     backgroundSize: 'cover',
//     backgroundPosition: 'center'
//   }}
// ></div>
//       {/* Celebration confetti */}
//       {/* {showCelebration && (
//         <div className="absolute inset-0 pointer-events-none z-50">
//           {[...Array(15)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute animate-bounce text-xl"
//               style={{
//                 left: `${Math.random() * 100}%`,
//                 top: `${Math.random() * 100}%`,
//                 animationDelay: `${Math.random() * 2}s`,
//                 animationDuration: `${2 + Math.random() * 2}s`
//               }}
//             >
//               {['🎉', '🎊', '✨', '🌟'][Math.floor(Math.random() * 4)]}
//             </div>
//           ))}
//         </div>
//       )} */}
      
//       <div className={`w-full ${isDesktop ? 'max-w-md' : 'max-w-xs'} text-center relative z-10`}>
//         {/* Professional Header */}
//         <div className="mb-1 space-y-1">
          // <div className="inline-flex items-center space-x-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-lg border border-red-200">
          //   <div className="w-5 h-5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
          //     <span className="text-white font-bold text-xs">H&M</span>
          //   </div>
          //   <span className="text-slate-700 font-medium text-sm">Exclusive Rewards</span>
          // </div>
          
//           <h1 className="text-slate-800 text-lg font-bold tracking-tight">
//             Scratch & Win
//           </h1>
//           {/* <p className="text-slate-600 text-sm max-w-xs mx-auto leading-relaxed">
//             Discover your exclusive H&M reward. Scratch to reveal your prize!
//           </p> */}
//         </div>

//         {/* Professional Scratch Card */}
//         <div className="mb-1 relative">
//           <div className="bg-white rounded-2xl shadow-xl border border-red-200 overflow-hidden">
//             <div className="bg-gradient-to-r from-red-50 to-red-100 px-1 py-1 border-b border-red-200">
//               <h3 className="text-slate-800 text-sm font-semibold">Your Reward Card</h3>
//               <p className="text-slate-500 text-xs ">Scratch below to reveal your prize</p>
//             </div>
            
//             <div className="p-1">
//               <div className={`relative w-full ${isDesktop ? 'h-28' : 'h-28'} bg-gradient-to-br from-slate-50 to-white rounded-xl border-2 border-dashed border-slate-200 overflow-hidden`}>
//                 {/* Prize Display */}
//                 {prize && (
//                   <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${prize.color} text-white p-4`}>
//                     <div className="text-lg ">{prize.image}</div>
//                     <h3 className="text-sm font-bold  text-center">{prize.title}</h3>
//                     <p className="text-white/90 text-sm text-center ">{prize.subtitle}</p>
//                     {prize.value && (
//                       <div className="bg-white/20 backdrop-blur-sm rounded-full px-1 py-1 border border-white/30">
//                         <span className="font-bold text-base">{prize.value}</span>
//                       </div>
//                     )}
//                   </div>
//                 )}
                
//                 {/* Scratch Canvas */}
//                 <canvas
//                   ref={canvasRef}
//                   className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
//                   onMouseDown={startScratch}
//                   onMouseMove={scratch}
//                   onMouseUp={endScratch}
//                   onMouseLeave={endScratch}
//                   onTouchStart={startScratch}
//                   onTouchMove={scratch}
//                   onTouchEnd={endScratch}
//                 />
//               </div>
              
//               {/* Professional Progress Indicator */}
//               {scratchPercentage > 0 && !isRevealed && (
//                 <div className="mt-1 space-y-1">
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-slate-600">Revealing...</span>
//                     <span className="text-sm font-medium text-slate-700">{Math.round(scratchPercentage)}%</span>
//                   </div>
//                   <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
//                     <div 
//                       className="bg-gradient-to-r from-red-400 to-red-500 h-2 rounded-full transition-all duration-300 ease-out"
//                       style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
//                     />
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
        
//         {/* Professional Action Buttons */}
        // <div className="space-y-1">
        //   {!isRevealed ? (
        //     <button 
        //       className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-1 px-1 text-base rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
        //       onClick={() => {
        //         if (!prize) generateRandomPrize();
        //       }}
        //     >
        //       <div className="flex items-center justify-center gap-1">
        //         <Sparkles size={16} />
        //         <span>Start Scratching</span>
        //       </div>
        //     </button>
        //   ) : (
        //     <div className="space-y-1">
        //       {prize?.value && (
        //         <button 
        //           className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold py-1 px-1 text-base rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
        //         >
        //           <div className="flex items-center justify-center gap-1">
        //             <Gift size={16} />
        //             <span>Claim Reward</span>
        //           </div>
        //         </button>
        //       )}
        //       <button 
        //         onClick={resetGame}
        //         className="w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-1 px-1 text-base rounded-xl shadow-lg border border-slate-200 transform hover:scale-105 transition-all duration-200"
        //       >
        //         <div className="flex items-center justify-center gap-1">
        //           <Target size={16} />
        //           <span>Try Again</span>
        //         </div>
        //       </button>
        //     </div>
        //   )}
        // </div>
        
//         {/* Professional Footer */}
//         <div className="mt-1 text-center">
//           <p className="text-slate-500 text-xs">
//             * Valid for 24 hours. Terms and conditions apply.
//           </p>
//         </div>
//       </div>
//     </div>
<div className="w-full h-full bg-gradient-to-b from-black via-slate-900 to-slate-800 text-white flex flex-col items-center justify-center p-4 relative overflow-hidden font-sans">
  {/* Gold sparkle background dots (optional effect layer) */}
  <div 
  className="absolute inset-0 opacity-30"
  style={{
    backgroundImage: `url('/assets/hm1.jpeg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }}
></div>
  <div className="relative z-10 w-full max-w-xs text-center space-y-2">
    {/* Header */}
    <div className="inline-flex items-center space-x-1 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-lg border border-red-200">
            <div className="w-8 h-5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xs">H&M</span>
            </div>
            <span className="text-slate-700 font-medium text-sm">Exclusive Rewards</span>
          </div>
    <div className="space-y-1">
      {/* <h2 className="text-sm tracking-wider text-yellow-400 font-medium">H&M PRIVE</h2> */}
      <h1 className="text-sm font-bold leading-tight">Scratch, Win & Shop in Style</h1>
      {/* <p className="text-xs text-slate-300 px-2">From premium discounts to surprise vouchers, reveal exclusive perks crafted just for you.</p> */}
    </div>

    {/* Scratch Reward Box */}
    <div className="bg-gradient-to-br from-yellow-200 via-yellow-300 to-yellow-400 rounded-xl p-1 space-y-2 text-black shadow-lg border border-yellow-500">
      <h3 className="text-sm font-semibold mt-2">YOUR REWARD AWAITS</h3>
      {/* <p className="text-xs font-medium text-center bg-yellow-100 border border-yellow-300 rounded-full px-2 py-1">
        Scratch to reveal what’s inside
      </p> */}

      {/* Scratch Prize Area */}
      <div className="relative w-full h-32 rounded-lg overflow-hidden border-2 border-dashed border-yellow-600 ">
        {prize && (
          <div className="absolute inset-0 flex flex-col justify-center items-center p-2 bg-yellow-100 text-black">
            <h4 className="font-bold text-sm">🎁 {prize.title}</h4>
            <p className="text-xs">{prize.subtitle}</p>
            <span className="mt-1 bg-yellow-300 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold">
              {prize.value}
            </span>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full cursor-crosshair"
          onMouseDown={startScratch}
          onMouseMove={scratch}
          onMouseUp={endScratch}
          onMouseLeave={endScratch}
          onTouchStart={startScratch}
          onTouchMove={scratch}
          onTouchEnd={endScratch}
        />
      </div>
    </div>

    {/* Action Buttons */}
    {/* <div className="space-y-2">
      {isRevealed && prize?.value ? (
        <>
          <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-red-600 hover:to-red-700 text-white font-semibold py-2 rounded-full shadow-lg transition-all duration-200">
            <Gift size={16} /> Claim My Reward
          </button>
          <button onClick={resetGame} className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-900 font-semibold py-2 rounded-full border border-slate-200 hover:bg-slate-200 transition">
            <RotateCcw size={16} /> Try Again
          </button>
        </>
      ) : (
        <button 
          onClick={() => { if (!prize) generateRandomPrize(); }}w-full flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:to-yellow-600 text-black font-semibold py-2 rounded-full shadow-lg transition-all duration-200
          className=""
        >
          <Sparkles size={16} /> Start Scratching
        </button>
      )}
    </div> */}
 <div className="space-y-1">
          {!isRevealed ? (
            <button 
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:to-yellow-600 text-black font-semibold py-2 rounded-full shadow-lg transition-all duration-200"
            //   onClick={() => {
            //     if (!prize) generateRandomPrize();
            //   }}
            // >
            onClick={() => {
              performDirectScratch();
            }}>
              <div className="flex items-center justify-center gap-1">
                <Sparkles size={16} />
                <span>Instant Scratching</span>
              </div>
            </button>
          ) : (
            <div className="space-y-1">
              {prize?.value && (
                <button 
                  className="w-full bg-gradient-to-r from-pink-500 to-red-600 hover:to-red-700 text-white font-medium py-1 rounded-full shadow-lg transition-all duration-200"
                >
                  <div className="flex items-center justify-center gap-1">
                    <Gift size={16} />
                    <span>Claim Reward</span>
                  </div>
                </button>
              )}
              <button 
                onClick={resetGame}
                className="w-full bg-slate-100 text-slate-900 font-medium py-1 rounded-full border border-slate-200 hover:bg-slate-200 transition"
              >
                <div className="flex items-center justify-center gap-1">
                  <Target size={16} />
                  <span>Try Again</span>
                </div>
              </button>
            </div>
          )}
        </div>
    {/* Footer */}
    <p className="text-xs text-slate-400">* Valid for 24 hours. T&Cs apply.</p>
  </div>
</div>

  );
};

function App() {
  const [isMobile, setIsMobile] = useState(true);

  return (
    <>
      <Helmet>
       <title>
  H&M Campaign | Scratch to Win Ad Showcase by Bidbox
        </title>
        <meta
          name="description"
          content="See how H&M uses Scratch to Win ads with Bidbox. Explore interactive campaigns that engage shoppers & boost brand excitement instantly"
        />
        {/* <meta
          name="keywords"
          content="BidBox AI, Demand-Side Platform, DSP solutions, targeted advertising, ad campaign optimization, programmatic advertising, real-time bidding, ad tech, audience segmentation, AI-driven advertising, digital marketing, performance marketing, cross-channel advertising, ad exchange, ad placement, ad inventory, ad network, data-driven marketing, media buying."
        /> */}
         <link
          rel="canonical"
          href="https://bidbox.ai/scratch-to-win/hm"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
    </Helmet>
    <div className="min-h-screen bg-gray-50 mt-10">
      <div className="container mx-auto px-8 py-8 lg:py-12">
        <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-12">
          {/* Left column - Content */}
          <div className="flex-1 lg:max-w-lg">
            {/* Header */}
            <header className="animate-slide-up">
              {/* Back to Ad Gallery */}
              {/* <div className="mb-6 lg:mb-8">
                <button className="flex items-center space-x-2 text-blue-500 hover:text-blue-600 transition-colors duration-200 group">
                  <ArrowLeft className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform duration-200" />
                  <span className="text-sm font-medium">Back to Ad Gallery</span>
                </button>
              </div> */}

              {/* Title and tag */}
              <div className="mb-4 lg:mt-16 md:mt-10 sm:mt-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                  Scratch. Reveal. Shop the Look.
                  </h1>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
           
                  Scratch to win

                  </span>
                </div>
                
                {/* View in preview site */}
                {/* <button className="flex items-center space-x-2 text-blue-500 hover:text-blue-600 transition-colors duration-200 group">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm font-medium group-hover:underline">       AKE IT IRRESISTIBLE</span>
                </button> */}
              </div>
            </header>

            {/* Content Section */}
            <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
              {/* Make it yours section */}
              <div className="mb-8">
                <h2 className="text-lg font-bold text-blue-600 mb-4 tracking-wide">
                MAKE IT IRRESISTIBLE
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6 max-w-md">
                This interactive Scratch & Win ad turns curiosity into clicks. Users scratch to reveal rewards like vouchers or discounts, right within the ad. It’s fast, fun, and fully customizable. Just add your branding and prizes, and you’re ready to launch a gamified experience that drives engagement.
                </p>
                
                {/* CTA Button */}
                <Link to='/contact'>
                <button className="group bg-white border-2 border-blue-500 text-blue-500 px-6 py-3 rounded-full font-medium hover:bg-blue-500 hover:text-white transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md transform hover:-translate-y-0.5">
                  <span>Start with this Template</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-200" />
                </button>
                </Link>
              </div>
            </div>
          </div>

          {/* Right column - Template Preview */}
          <div className="flex-1 lg:max-w-2xl mt-8 lg:mt-0">
            {/* Preview toggle */}
            {/* <div className="flex justify-center mt-2 animate-fade-in mb-4" style={{ animationDelay: '0.3s' }}> */}
            <div className="hidden lg:flex justify-center mt-2 animate-fade-in mb-4" style={{ animationDelay: '0.3s' }}>

                <div className="bg-white rounded-full px-6 py-3 shadow-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                    {/* <span className="text-sm text-gray-600 font-medium">Preview in:</span> */}
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium transition-colors ${isMobile ? 'text-blue-600' : 'text-gray-600'}`}>
                        Mobile
                      </span>
                      <button
                        onClick={() => setIsMobile(!isMobile)}
                        className="relative w-12 h-6 bg-blue-500 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${isMobile ? 'left-1' : 'left-7'}`}></div>
                      </button>
                      <span className={`text-sm font-medium transition-colors ${!isMobile ? 'text-blue-600' : 'text-gray-600'}`}>
                        Desktop
                      </span>
                    </div>
                  </div>
                </div>
              </div>
           
            <div className="relative animate-fade-in" style={{ animationDelay: '0.4s' }}>
              
              
              {/* Mobile Mockup */}
              {isMobile && (
                <div className="mx-auto max-w-xs">
                  {/* iPhone mockup */}
                  <div className="relative bg-gray-200  rounded-[2.5rem] p-2 shadow-2xl">
                    {/* Status bar area */}
                    <div className="bg-white rounded-[2rem] overflow-hidden">
                      {/* Status bar */}
                      <div className="bg-white px-6 py-2 flex justify-between items-center text-black text-sm font-medium">
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                          <div className="flex space-x-1">
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                            <div className="w-1 h-1 bg-black rounded-full"></div>
                          </div>
                          <div className="w-6 h-3 border border-black rounded-sm relative">
                            <div className="w-4/5 h-full bg-green-500 rounded-sm"></div>
                          </div>
                        </div>
                      </div>

                      {/* Browser bar */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center space-x-3">
                        <div className="flex-1 bg-white rounded-lg px-3 py-2 text-sm text-gray-600 flex items-center justify-center">
                          hm.com/scratch-win
                        </div>
                      </div>

                      {/* Content area */}
                      <div className="p-4 bg-gray-50 h-[400px] overflow-y-auto hide-scrollbar">
                        {/* Scratch Game - Full screen on mobile */}
                        <div className="space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                  </div>
                  
                        <div className="mb-6">
                          <div className="relative group transform transition-all duration-300 rounded-lg overflow-hidden">
                            <ScratchToWin isDesktop={false} />
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                        <div className="space-y-3 mt-2">
                          <div className="h-3 bg-gray-200 rounded w-full"></div>
                          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                          <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                        </div>
                      </div>

                      {/* Bottom navigation */}
                      {/* <div className="bg-gray-100 px-4 py-3 flex justify-center space-x-6 border-t border-gray-200">
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <ArrowLeft className="w-5 h-5" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <ArrowRight className="w-5 h-5" />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                          </svg>
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                          </svg>
                        </button>
                      </div> */}
                    </div>
                  </div>
                </div>
              )}

              {/* Desktop Mockup */}
              {!isMobile && (
                <div className="w-full max-w-4xl mx-auto hidden lg:block">
                                {/* <div className={`hidden lg:${!isMobile ? 'block' : 'hidden'} w-full max-w-4xl mx-auto`}> */}
                  {/* Desktop Screen Frame */}
                  <div className="bg-gray-200 rounded-xl p-3 shadow-2xl">
                    {/* Screen */}
                    <div className="bg-white rounded-lg overflow-hidden shadow-inner" style={{ aspectRatio: '16/10' }}>
                      {/* macOS-style window header */}
                      <div className="bg-gray-100 px-4 py-3 flex items-center border-b border-gray-200">
                        <div className="flex space-x-2">
                          <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                          <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                        </div>
                        <div className="flex-1 mx-2">
                          <div className="bg-white rounded-md px-2 py-1 text-sm text-gray-600 border shadow-sm max-w-md">
                            🔒 hm.com/scratch-win
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                          <div className="w-4 h-4 bg-gray-300 rounded"></div>
                        </div>
                      </div>

                      {/* Browser content area */}
                      <div className="p-2 bg-gray-50 h-[500px] overflow-y-auto hide-scrollbar">
                        {/* Page header */}
                        {/* <div className="mb-2 text-center">
                          <h1 className="text-2xl font-bold text-gray-800 mb-2">H&M Exclusive Rewards</h1>
                          <p className="text-gray-600">Scratch to reveal your special offer!</p>
                        </div> */}
 <div className="mb-2">
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6 mb-2"></div>

                          </div>
                        {/* Scratch Game centered on desktop */}
                        <div className="flex justify-center">
                          <div className="w-full max-w-md">
                            <ScratchToWin isDesktop={true} />
                          </div>
                        </div>
                           {/* Additional content placeholders */}
                           <div className="grid grid-cols-2 gap-6 mb-6 mt-2">
                            <div className="space-y-3">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                            <div className="space-y-3 mt-2">
                              <div className="h-4 bg-gray-200 rounded w-full"></div>
                              <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                              <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                            </div>
                          </div>

                          {/* Sidebar placeholders */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                            <div className="h-24 bg-gray-200 rounded"></div>
                          </div>
                          <div className="grid grid-cols-2 gap-6 mb-6">
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                          <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-full"></div>
                            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
                          </div>
                        </div>

                        {/* Sidebar placeholders */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                          <div className="h-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Monitor stand */}
                  <div className="flex justify-center">
                    <div className="w-20 h-4 bg-gray-200 rounded-b-lg"></div>
                  </div>
                  <div className="flex justify-center">
                    <div className="w-32 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                </div>
              )}

             
            </div>
          </div>
        </div>
      </div>
      </div>
         <div className="max-w-7xl mx-auto px-4 py-10">
                <h1 className="text-3xl font-bold text-center text-gray-800 mb-10">Discover More Ads</h1>
          
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {/* Card 1 */}
                  <Link to="/quiz/ramadan" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                    <img
                      src="/assets/rama.webp"
                      alt="Card 1"
                      className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <h2 className="text-lg font-semibold">Ramadan</h2>
                    </div>
                  </Link>
          
                  {/* Card 2 */}
                  <Link to="/dynamic-calculator/zakat" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                    <img
                      src="/assets/zakat.webp"
                      alt="Card 2"
                      className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <h2 className="text-lg font-semibold">Zakat Calculator</h2>
                    </div>
                  </Link>
          
                  {/* Card 3 */}
                  <Link to="/interactive-selector/donation" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                    <img
                      src="/assets/donation.png "
                      alt="Card 3"
                      className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <h2 className="text-lg font-semibold">Donation</h2>
                    </div>
                  </Link>
          
                  {/* Card 4 */}
                  <Link to="/spin-the-wheel/mamaearth" className="block group rounded-xl overflow-hidden shadow-lg transition-transform duration-300 transform hover:scale-105 hover:shadow-2xl">
                    <img
                      src="/assets/mam1.jpg"
                      alt="Card 4"
                      className="w-full h-60 object-cover transition-opacity duration-300 group-hover:opacity-80"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <h2 className="text-lg font-semibold">Mama Earth</h2>
                    </div>
                  </Link>
          
                 
                </div>
              </div>
      </>

    );
}

export default App;


// import React, { useState, useRef, useEffect } from 'react';
// import { ChevronLeft, ChevronRight, Copy, Play, Pause, Maximize2, Download, Share2, Eye, Settings, Monitor, Smartphone, Gift, Star, Heart, Sparkles, Trophy, Target } from 'lucide-react';

// // Scratch to Win Component
// const ScratchToWin = ({ isDesktop = false }) => {
//   const canvasRef = useRef(null);
//   const [isScratching, setIsScratching] = useState(false);
//   const [scratchPercentage, setScratchPercentage] = useState(0);
//   const [isRevealed, setIsRevealed] = useState(false);
//   const [prize, setPrize] = useState(null);
//   const [showCelebration, setShowCelebration] = useState(false);

//   const prizes = [
//     { 
//       id: 1, 
//       title: "H&M Premium Discount", 
//       subtitle: "30% OFF on Your Next Purchase", 
//       image: "🎁",
//       color: "from-emerald-500 to-emerald-600",
//       textColor: "text-emerald-700",
//       value: "30% OFF"
//     },
//     { 
//       id: 2, 
//       title: "H&M Fashion Voucher", 
//       subtitle: "$50 Fashion Credit", 
//       image: "💎",
//       color: "from-purple-500 to-purple-600",
//       textColor: "text-purple-700",
//       value: "$50"
//     },
//     { 
//       id: 3, 
//       title: "Better Luck Next Time", 
//       subtitle: "Try again tomorrow for new prizes!", 
//       image: "🎯",
//       color: "from-slate-400 to-slate-500",
//       textColor: "text-slate-700",
//       value: null
//     },
//   ];

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     if (!canvas) return;

//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     canvas.width = rect.width * window.devicePixelRatio;
//     canvas.height = rect.height * window.devicePixelRatio;
//     ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
//     // Create professional scratch overlay with subtle texture
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#e5e7eb');
//     gradient.addColorStop(0.3, '#d1d5db');
//     gradient.addColorStop(0.7, '#9ca3af');
//     gradient.addColorStop(1, '#6b7280');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add subtle texture pattern
//     ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
//     for (let i = 0; i < rect.width; i += 4) {
//       for (let j = 0; j < rect.height; j += 4) {
//         if (Math.random() > 0.7) {
//           ctx.fillRect(i, j, 1, 1);
//         }
//       }
//     }
    
//     // Professional text styling
//     ctx.fillStyle = '#374151';
//     ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
//     ctx.textAlign = 'center';
//     ctx.letterSpacing = '0.5px';
//     ctx.fillText('SCRATCH TO REVEAL', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
//     ctx.fillStyle = '#6b7280';
//     ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
//     ctx.fillText('Your exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
//   }, [isDesktop]);

//   const generateRandomPrize = () => {
//     const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
//     setPrize(randomPrize);
//     if (randomPrize.value) {
//       setShowCelebration(true);
//       setTimeout(() => setShowCelebration(false), 3000);
//     }
//   };

//   const scratch = (e) => {
//     if (!isScratching) return;
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     let clientX, clientY;
//     if (e.type.includes('touch')) {
//       clientX = e.touches[0].clientX;
//       clientY = e.touches[0].clientY;
//     } else {
//       clientX = e.clientX;
//       clientY = e.clientY;
//     }
    
//     const x = clientX - rect.left;
//     const y = clientY - rect.top;
    
//     ctx.globalCompositeOperation = 'destination-out';
//     ctx.beginPath();
//     ctx.arc(x, y, isDesktop ? 35 : 25, 0, 2 * Math.PI);
//     ctx.fill();
    
//     // Calculate scratch percentage
//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
//     const pixels = imageData.data;
//     let transparent = 0;
    
//     for (let i = 3; i < pixels.length; i += 4) {
//       if (pixels[i] === 0) transparent++;
//     }
    
//     const percentage = (transparent / (pixels.length / 4)) * 100;
//     setScratchPercentage(percentage);
    
//     if (percentage > 35 && !isRevealed) {
//       setIsRevealed(true);
//       if (!prize) generateRandomPrize();
//     }
//   };

//   const startScratch = (e) => {
//     e.preventDefault();
//     setIsScratching(true);
//     if (!prize) generateRandomPrize();
//   };

//   const endScratch = () => {
//     setIsScratching(false);
//   };

//   const resetGame = () => {
//     setIsRevealed(false);
//     setPrize(null);
//     setScratchPercentage(0);
//     setShowCelebration(false);
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     ctx.globalCompositeOperation = 'source-over';
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#e5e7eb');
//     gradient.addColorStop(0.3, '#d1d5db');
//     gradient.addColorStop(0.7, '#9ca3af');
//     gradient.addColorStop(1, '#6b7280');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add texture pattern
//     ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
//     for (let i = 0; i < rect.width; i += 4) {
//       for (let j = 0; j < rect.height; j += 4) {
//         if (Math.random() > 0.7) {
//           ctx.fillRect(i, j, 1, 1);
//         }
//       }
//     }
    
//     ctx.fillStyle = '#374151';
//     ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
//     ctx.textAlign = 'center';
//     ctx.fillText('SCRATCH TO REVEAL', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
//     ctx.fillStyle = '#6b7280';
//     ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
//     ctx.fillText('Your exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
//   };

//   return (
//     <div className="w-full h-full bg-gradient-to-br from-slate-50 via-red-50 to-red-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
//       {/* Subtle background elements */}
//       <div className="absolute inset-0 opacity-10">
//         <div className="absolute top-20 left-20 w-32 h-32 bg-red-300 rounded-full blur-3xl"></div>
//         <div className="absolute bottom-20 right-20 w-48 h-48 bg-red-400 rounded-full blur-3xl"></div>
//       </div>

//       {/* Celebration confetti */}
//       {showCelebration && (
//         <div className="absolute inset-0 pointer-events-none z-50">
//           {[...Array(15)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute animate-bounce text-xl"
//               style={{
//                 left: `${Math.random() * 100}%`,
//                 top: `${Math.random() * 100}%`,
//                 animationDelay: `${Math.random() * 2}s`,
//                 animationDuration: `${2 + Math.random() * 2}s`
//               }}
//             >
//               {['🎉', '🎊', '✨', '🌟'][Math.floor(Math.random() * 4)]}
//             </div>
//           ))}
//         </div>
//       )}
      
//       <div className="w-full max-w-xs text-center relative z-10">
//         {/* Professional Header */}
//         <div className="mb-4 space-y-2">
//           <div className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg border border-red-200">
//             <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
//               <span className="text-white font-bold text-xs">H&M</span>
//             </div>
//             <span className="text-slate-700 font-medium text-sm">Exclusive Rewards</span>
//           </div>
          
//           <h1 className="text-slate-800 text-2xl font-bold tracking-tight">
//             Scratch & Win
//           </h1>
//           <p className="text-slate-600 text-sm max-w-xs mx-auto leading-relaxed">
//             Discover your exclusive H&M reward. Scratch to reveal your prize!
//           </p>
//         </div>

//         {/* Professional Scratch Card */}
//         <div className="mb-4 relative">
//           <div className="bg-white rounded-2xl shadow-xl border border-red-200 overflow-hidden">
//             <div className="bg-gradient-to-r from-red-50 to-red-100 px-4 py-3 border-b border-red-200">
//               <h3 className="text-slate-800 text-lg font-semibold">Your Reward Card</h3>
//               <p className="text-slate-500 text-xs mt-1">Scratch below to reveal your prize</p>
//             </div>
            
//             <div className="p-4">
//               <div className="relative w-full h-40 bg-gradient-to-br from-slate-50 to-white rounded-xl border-2 border-dashed border-slate-200 overflow-hidden">
//                 {/* Prize Display */}
//                 {prize && (
//                   <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${prize.color} text-white p-4`}>
//                     <div className="text-3xl mb-2">{prize.image}</div>
//                     <h3 className="text-lg font-bold mb-1 text-center">{prize.title}</h3>
//                     <p className="text-white/90 text-sm text-center mb-2">{prize.subtitle}</p>
//                     {prize.value && (
//                       <div className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 border border-white/30">
//                         <span className="font-bold text-base">{prize.value}</span>
//                       </div>
//                     )}
//                   </div>
//                 )}
                
//                 {/* Scratch Canvas */}
//                 <canvas
//                   ref={canvasRef}
//                   className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
//                   onMouseDown={startScratch}
//                   onMouseMove={scratch}
//                   onMouseUp={endScratch}
//                   onMouseLeave={endScratch}
//                   onTouchStart={startScratch}
//                   onTouchMove={scratch}
//                   onTouchEnd={endScratch}
//                 />
//               </div>
              
//               {/* Professional Progress Indicator */}
//               {scratchPercentage > 0 && !isRevealed && (
//                 <div className="mt-3 space-y-2">
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-slate-600">Revealing...</span>
//                     <span className="text-sm font-medium text-slate-700">{Math.round(scratchPercentage)}%</span>
//                   </div>
//                   <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
//                     <div 
//                       className="bg-gradient-to-r from-red-400 to-red-500 h-2 rounded-full transition-all duration-300 ease-out"
//                       style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
//                     />
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
        
//         {/* Professional Action Buttons */}
//         <div className="space-y-3">
//           {!isRevealed ? (
//             <button 
//               className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 px-4 text-base rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
//               onClick={() => {
//                 if (!prize) generateRandomPrize();
//               }}
//             >
//               <div className="flex items-center justify-center gap-2">
//                 <Sparkles size={16} />
//                 <span>Start Scratching</span>
//               </div>
//             </button>
//           ) : (
//             <div className="space-y-2">
//               {prize?.value && (
//                 <button 
//                   className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold py-3 px-4 text-base rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
//                 >
//                   <div className="flex items-center justify-center gap-2">
//                     <Gift size={16} />
//                     <span>Claim Reward</span>
//                   </div>
//                 </button>
//               )}
//               <button 
//                 onClick={resetGame}
//                 className="w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold py-2 px-4 text-base rounded-xl shadow-lg border border-slate-200 transform hover:scale-105 transition-all duration-200"
//               >
//                 <div className="flex items-center justify-center gap-2">
//                   <Target size={16} />
//                   <span>Try Again</span>
//                 </div>
//               </button>
//             </div>
//           )}
//         </div>
        
//         {/* Professional Footer */}
//         <div className="mt-4 text-center">
//           <p className="text-slate-500 text-xs">
//             * Valid for 24 hours. Terms and conditions apply.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default function ScratchToWinMockup() {
//     const [selectedDevice, setSelectedDevice] = useState('Mobile');

//     return (
//         <div className="mt-14 min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-red-100 p-6">
//             {/* Header */}
//             <div className="mb-8">
//                 <div className="flex items-center justify-between">
//                     <div>
//                         <h1 className="text-3xl font-bold text-gray-900 mb-2">H&M Scratch & Win Campaign</h1>
//                         <p className="text-gray-600">Interactive scratch-off game with prize rewards</p>
//                     </div>
//                     <div className="flex items-center gap-3">
//                         <button className="px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2">
//                             <Settings className="h-4 w-4" />
//                             Settings
//                         </button>
//                         <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2">
//                             <Share2 className="h-4 w-4" />
//                             Share
//                         </button>
//                     </div>
//                 </div>
//             </div>

//             <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
//                 {/* Left Sidebar - Project Info */}
//                 <div className="lg:col-span-1">
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
//                         <div className="flex items-center gap-3 mb-4">
//                             <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
//                                 <Gift className="h-6 w-6 text-white" />
//                             </div>
//                             <div>
//                                 <h2 className="text-lg font-semibold text-gray-900">H&M Scratch & Win</h2>
//                                 <p className="text-sm text-gray-500">ID: 789123</p>
//                             </div>
//                         </div>
                        
//                         <div className="space-y-3">
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Status</span>
//                                 <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Active</span>
//                             </div>
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Campaign</span>
//                                 <span className="text-sm text-gray-900">Holiday 2024</span>
//                             </div>
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Updated</span>
//                                 <span className="text-sm text-gray-900">1 hour ago</span>
//                             </div>
//                         </div>

//                         <button className="w-full mt-4 text-red-600 hover:text-red-700 text-sm font-medium">
//                             View Campaign Analytics
//                         </button>
//                     </div>

//                     {/* Quick Actions */}
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
//                         <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
//                         <div className="space-y-3">
//                             <button className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-left text-sm transition-colors flex items-center gap-2">
//                                 <Download className="h-4 w-4" />
//                                 Export Creative
//                             </button>
//                             <button className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-left text-sm transition-colors flex items-center gap-2">
//                                 <Copy className="h-4 w-4" />
//                                 Copy Ad Tag
//                             </button>
//                             <button className="w-full px-4 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-left text-sm transition-colors flex items-center gap-2">
//                                 <Maximize2 className="h-4 w-4" />
//                                 Fullscreen Test
//                             </button>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Center Content - Preview Area */}
//                 <div className="lg:col-span-2">
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
//                         {/* Device Toggle */}
//                         <div className="flex items-center justify-between mb-6">
//                             <div className="flex items-center gap-2">
//                                 <h3 className="text-lg font-semibold text-gray-900">Interactive Preview</h3>
//                                 <Copy className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors" />
//                             </div>
//                             <div className="flex items-center bg-gray-100 rounded-lg p-1">
//                                 {[
//                                     { name: 'Mobile', icon: Smartphone },
//                                     { name: 'Desktop', icon: Monitor }
//                                 ].map(({ name, icon: Icon }) => (
//                                     <button
//                                         key={name}
//                                         className={`px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
//                                             selectedDevice === name
//                                                 ? 'bg-white text-red-600 shadow-sm'
//                                                 : 'text-gray-600 hover:text-gray-900'
//                                         }`}
//                                         onClick={() => setSelectedDevice(name)}
//                                     >
//                                         <Icon className="h-4 w-4" />
//                                         {name}
//                                     </button>
//                                 ))}
//                             </div>
//                         </div>

//                         {/* Navigation */}
//                         <div className="flex items-center justify-between mb-6">
//                             <button className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors">
//                                 <ChevronLeft className="h-4 w-4" />
//                                 Previous Ad
//                             </button>
//                             <div className="flex items-center gap-2">
//                                 <div className="w-2 h-2 bg-red-600 rounded-full"></div>
//                                 <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
//                                 <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
//                             </div>
//                             <button className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors">
//                                 Next Ad
//                                 <ChevronRight className="h-4 w-4" />
//                             </button>
//                         </div>

//                         {/* Preview Container */}
//                         <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 min-h-[600px] flex items-center justify-center">
//                             {/* Device Frame */}
//                             <div className="relative">
//                                 {selectedDevice === 'Mobile' ? (
//                                     <div className="relative">
//                                         <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-red-600/20 rounded-3xl blur-xl"></div>
//                                         <div className="relative bg-gray-900 rounded-3xl p-2 shadow-2xl">
//                                             <div className="bg-black rounded-2xl overflow-hidden">
//                                                 <div className="w-[280px] h-[560px] relative">
//                                                     <ScratchToWin isDesktop={false} />
//                                                 </div>
//                                             </div>
//                                             <div className="w-20 h-1 bg-gray-600 rounded-full mx-auto mt-2"></div>
//                                         </div>
//                                     </div>
//                                 ) : (
//                                     <div className="relative">
//                                         <div className="absolute -inset-4 bg-gradient-to-r from-red-500/20 to-red-600/20 rounded-xl blur-xl"></div>
//                                         <div className="relative bg-white rounded-xl shadow-2xl p-4">
//                                             <div className="bg-gray-100 rounded-lg p-2">
//                                                 <div className="w-[400px] h-[600px] bg-black rounded overflow-hidden relative">
//                                                     <ScratchToWin isDesktop={true} />
//                                                 </div>
//                                             </div>
//                                         </div>
//                                     </div>
//                                 )}
//                             </div>
//                         </div>

//                         {/* Interaction Guide */}
//                         <div className="mt-6 bg-red-50 rounded-lg p-4">
//                             <div className="flex items-center gap-3">
//                                 <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center">
//                                     <Gift className="h-4 w-4" />
//                                 </div>
//                                 <div>
//                                     <h4 className="font-semibold text-red-900">Interactive Scratch Game</h4>
//                                     <p className="text-sm text-red-700">Click and drag to scratch the card surface and reveal your H&M prize</p>
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 </div>

//                 {/* Right Sidebar - Details */}
//                 <div className="lg:col-span-1">
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
//                         <h3 className="text-lg font-semibold text-gray-900 mb-4">Creative Details</h3>
                        
//                         <div className="space-y-4">
//                             <div className="bg-gray-50 rounded-lg p-3">
//                                 <div className="text-sm text-gray-600 mb-1">Dimensions</div>
//                                 <div className="text-lg font-semibold text-gray-900">280 × 560px</div>
//                             </div>
                            
//                             <div className="bg-gray-50 rounded-lg p-3">
//                                 <div className="text-sm text-gray-600 mb-1">Format</div>
//                                 <div className="text-lg font-semibold text-gray-900">HTML5 Canvas</div>
//                             </div>
                            
//                             <div className="bg-gray-50 rounded-lg p-3">
//                                 <div className="text-sm text-gray-600 mb-1">Interaction</div>
//                                 <div className="text-lg font-semibold text-gray-900">Scratch-off Game</div>
//                             </div>
                            
//                             <div className="bg-gray-50 rounded-lg p-3">
//                                 <div className="text-sm text-gray-600 mb-1">File Size</div>
//                                 <div className="text-lg font-semibold text-gray-900">1.8 MB</div>
//                             </div>
//                         </div>
//                     </div>

//                     {/* Performance Metrics */}
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
//                         <h3 className="text-lg font-semibold text-gray-900 mb-4">Game Stats</h3>
//                         <div className="space-y-3">
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Completion Rate</span>
//                                 <span className="text-sm font-semibold text-green-600">87%</span>
//                             </div>
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Win Rate</span>
//                                 <span className="text-sm font-semibold text-blue-600">66%</span>
//                             </div>
//                             <div className="flex justify-between items-center">
//                                 <span className="text-sm text-gray-600">Avg. Time</span>
//                                 <span className="text-sm font-semibold text-purple-600">45s</span>
//                             </div>
//                         </div>
//                     </div>

//                     {/* Prize Distribution */}
//                     <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
//                         <h3 className="text-lg font-semibold text-gray-900 mb-4">Prize Distribution</h3>
//                         <div className="space-y-3">
//                             <div className="flex items-center gap-3">
//                                 <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
//                                 <span className="text-sm text-gray-600">30% OFF - 40% chance</span>
//                             </div>
//                             <div className="flex items-center gap-3">
//                                 <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
//                                 <span className="text-sm text-gray-600">$50 Credit - 25% chance</span>
//                             </div>
//                             <div className="flex items-center gap-3">
//                                 <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
//                                 <span className="text-sm text-gray-600">No Prize - 35% chance</span>
//                             </div>
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }

// import React, { useState, useRef, useEffect } from 'react';

// const ScratchToWin = ({ isDesktop = false }) => {
//   const canvasRef = useRef(null);
//   const [isScratching, setIsScratching] = useState(false);
//   const [scratchPercentage, setScratchPercentage] = useState(0);
//   const [isRevealed, setIsRevealed] = useState(false);
//   const [prize, setPrize] = useState(null);
//   const [showCelebration, setShowCelebration] = useState(false);

//   const prizes = [
//     { 
//       id: 1, 
//       title: "H&M Premium Discount", 
//       subtitle: "30% OFF on Your Next Purchase", 
//       image: "🎁",
//       color: "from-emerald-500 to-emerald-600",
//       textColor: "text-emerald-700",
//       value: "30% OFF"
//     },
//     { 
//       id: 2, 
//       title: "H&M Fashion Voucher", 
//       subtitle: "$50 Fashion Credit", 
//       image: "💎",
//       color: "from-purple-500 to-purple-600",
//       textColor: "text-purple-700",
//       value: "$50"
//     },
//     { 
//       id: 3, 
//       title: "Better Luck Next Time", 
//       subtitle: "Try again tomorrow for new prizes!", 
//       image: "🎯",
//       color: "from-slate-400 to-slate-500",
//       textColor: "text-slate-700",
//       value: null
//     },
//   ];

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     if (!canvas) return;

//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     canvas.width = rect.width * window.devicePixelRatio;
//     canvas.height = rect.height * window.devicePixelRatio;
//     ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
//     // Create professional scratch overlay with subtle texture
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#e5e7eb');
//     gradient.addColorStop(0.3, '#d1d5db');
//     gradient.addColorStop(0.7, '#9ca3af');
//     gradient.addColorStop(1, '#6b7280');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add subtle texture pattern
//     ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
//     for (let i = 0; i < rect.width; i += 4) {
//       for (let j = 0; j < rect.height; j += 4) {
//         if (Math.random() > 0.7) {
//           ctx.fillRect(i, j, 1, 1);
//         }
//       }
//     }
    
//     // Professional text styling
//     ctx.fillStyle = '#374151';
//     ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
//     ctx.textAlign = 'center';
//     ctx.letterSpacing = '0.5px';
//     ctx.fillText('SCRATCH TO REVEAL', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
//     ctx.fillStyle = '#6b7280';
//     ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
//     ctx.fillText('Your exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
//   }, [isDesktop]);

//   const generateRandomPrize = () => {
//     const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
//     setPrize(randomPrize);
//     if (randomPrize.value) {
//       setShowCelebration(true);
//       setTimeout(() => setShowCelebration(false), 3000);
//     }
//   };

//   const scratch = (e) => {
//     if (!isScratching) return;
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     let clientX, clientY;
//     if (e.type.includes('touch')) {
//       clientX = e.touches[0].clientX;
//       clientY = e.touches[0].clientY;
//     } else {
//       clientX = e.clientX;
//       clientY = e.clientY;
//     }
    
//     const x = clientX - rect.left;
//     const y = clientY - rect.top;
    
//     ctx.globalCompositeOperation = 'destination-out';
//     ctx.beginPath();
//     ctx.arc(x, y, isDesktop ? 35 : 25, 0, 2 * Math.PI);
//     ctx.fill();
    
//     // Calculate scratch percentage
//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
//     const pixels = imageData.data;
//     let transparent = 0;
    
//     for (let i = 3; i < pixels.length; i += 4) {
//       if (pixels[i] === 0) transparent++;
//     }
    
//     const percentage = (transparent / (pixels.length / 4)) * 100;
//     setScratchPercentage(percentage);
    
//     if (percentage > 35 && !isRevealed) {
//       setIsRevealed(true);
//       if (!prize) generateRandomPrize();
//     }
//   };

//   const startScratch = (e) => {
//     e.preventDefault();
//     setIsScratching(true);
//     if (!prize) generateRandomPrize();
//   };

//   const endScratch = () => {
//     setIsScratching(false);
//   };

//   const resetGame = () => {
//     setIsRevealed(false);
//     setPrize(null);
//     setScratchPercentage(0);
//     setShowCelebration(false);
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     ctx.globalCompositeOperation = 'source-over';
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#e5e7eb');
//     gradient.addColorStop(0.3, '#d1d5db');
//     gradient.addColorStop(0.7, '#9ca3af');
//     gradient.addColorStop(1, '#6b7280');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add texture pattern
//     ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
//     for (let i = 0; i < rect.width; i += 4) {
//       for (let j = 0; j < rect.height; j += 4) {
//         if (Math.random() > 0.7) {
//           ctx.fillRect(i, j, 1, 1);
//         }
//       }
//     }
    
//     ctx.fillStyle = '#374151';
//     ctx.font = `600 ${isDesktop ? '24px' : '18px'} system-ui, -apple-system, sans-serif`;
//     ctx.textAlign = 'center';
//     ctx.fillText('SCRATCH TO REVEAL', rect.width / 2, rect.height / 2 - (isDesktop ? 20 : 15));
    
//     ctx.fillStyle = '#6b7280';
//     ctx.font = `400 ${isDesktop ? '16px' : '14px'} system-ui, -apple-system, sans-serif`;
//     ctx.fillText('Your exclusive H&M offer awaits', rect.width / 2, rect.height / 2 + (isDesktop ? 25 : 20));
//   };

//   return (
//     <div className=" min-h-screen bg-gradient-to-br from-slate-700 via-slate-400 to-slate-300  flex flex-col items-center justify-center p-4 relative overflow-hidden">
//       {/* Subtle background elements */}
//       <div 
//   className="absolute inset-0 opacity-30"
//   style={{
//     backgroundImage: `url('/assets/hm1.jpg')`,
//     backgroundSize: 'cover',
//     backgroundPosition: 'center'
//   }}
// ></div>
//       <div className="absolute inset-0 opacity-5">
//         <div className="absolute top-20 left-20 w-64 h-64 bg-slate-300 rounded-full blur-3xl"></div>
//         <div className="absolute bottom-20 right-20 w-96 h-96 bg-slate-400 rounded-full blur-3xl"></div>
//       </div>

//       {/* Celebration confetti */}
//       {/* {showCelebration && (
//         <div className="absolute inset-0 pointer-events-none z-50">
//           {[...Array(20)].map((_, i) => (
//             <div
//               key={i}
//               className="absolute animate-bounce"
//               style={{
//                 left: `${Math.random() * 100}%`,
//                 top: `${Math.random() * 100}%`,
//                 animationDelay: `${Math.random() * 2}s`,
//                 animationDuration: `${2 + Math.random() * 2}s`
//               }}
//             >
//               {['🎉', '🎊', '✨', '🌟'][Math.floor(Math.random() * 4)]}
//             </div>
//           ))}
//         </div>
//       )} */}
      
//       <div className={`${isDesktop ? 'max-w-xl' : 'max-w-sm'} w-full text-center relative z-10`}>
//         {/* Professional Header */}
//         <div className={`${isDesktop ? 'mb-2' : 'mb-1'} space-y-1`}>
//           <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-2 shadow-lg border border-slate-200">
//             <div className="w-6 h-6 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
//               <span className="text-white font-bold text-sm">H&M</span>
//             </div>
//             <span className="text-slate-700 font-medium">Exclusive Partner Rewards</span>
//           </div>
          
//           <h1 className={`text-slate-800 ${isDesktop ? 'text-3xl' : 'text-2xl'} font-bold tracking-tight`}>
//             Scratch & Win
//           </h1>
//           <p className={`text-slate-900 ${isDesktop ? 'text-lg' : 'text-base'} max-w-md mx-auto leading-relaxed`}>
//             Discover your exclusive H&M reward. Each scratch reveals a unique offer crafted just for you.
//           </p>
//         </div>

//         {/* Professional Scratch Card */}
//         <div className={`${isDesktop ? 'mb-4' : 'mb-2'} relative`}>
//           <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden">
//             <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-4 py-1 border-b border-slate-200">
//               <h3 className={`text-slate-800 ${isDesktop ? 'text-xl' : 'text-lg'} font-semibold`}>Your Reward Card</h3>
//               <p className="text-slate-500 text-sm mt-0.5">Scratch the area below to reveal your prize</p>
//             </div>
            
//             <div className="p-6">
//               <div className={`relative w-full ${isDesktop ? 'h-48' : 'h-48'} bg-gradient-to-br from-slate-50 to-white rounded-2xl border-2 border-dashed border-slate-200 overflow-hidden`}>
//                 {/* Prize Display */}
//                 {prize && (
//                   <div className={`absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br ${prize.color} text-white p-6`}>
//                     <div className={`${isDesktop ? 'text-4xl mb-2' : 'text-3xl mb-2'}`}>{prize.image}</div>
//                     <h3 className={`${isDesktop ? 'text-2xl' : 'text-lg'} font-bold mb-1 text-center`}>{prize.title}</h3>
//                     <p className={`text-white/90 ${isDesktop ? 'text-base' : 'text-sm'} text-center mb-1`}>{prize.subtitle}</p>
//                     {prize.value && (
//                       <div className={`bg-white/20 backdrop-blur-sm rounded-full ${isDesktop ? 'px-6 py-1' : 'px-4 py-1'} border border-white/30`}>
//                         <span className={`font-bold ${isDesktop ? 'text-lg' : 'text-base'}`}>{prize.value}</span>
//                       </div>
//                     )}
//                   </div>
//                 )}
                
//                 {/* Scratch Canvas */}
//                 <canvas
//                   ref={canvasRef}
//                   className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
//                   onMouseDown={startScratch}
//                   onMouseMove={scratch}
//                   onMouseUp={endScratch}
//                   onMouseLeave={endScratch}
//                   onTouchStart={startScratch}
//                   onTouchMove={scratch}
//                   onTouchEnd={endScratch}
//                 />
//               </div>
              
//               {/* Professional Progress Indicator */}
//               {scratchPercentage > 0 && !isRevealed && (
//                 <div className="mt-2 space-y-2">
//                   <div className="flex justify-between items-center">
//                     <span className="text-sm text-slate-600">Revealing...</span>
//                     <span className="text-sm font-medium text-slate-700">{Math.round(scratchPercentage)}%</span>
//                   </div>
//                   <div className="bg-slate-200 rounded-full h-2 overflow-hidden">
//                     <div 
//                       className="bg-gradient-to-r from-slate-400 to-slate-500 h-2 rounded-full transition-all duration-300 ease-out"
//                       style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
//                     />
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
        
//         {/* Professional Action Buttons */}
//         <div className="space-y-1">
//           {!isRevealed ? (
//             <button 
//               className={`w-full bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white font-semibold ${isDesktop ? 'py-2 px-4 text-lg' : 'py-2 px-3 text-base'} rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 border border-slate-600`}
//               onClick={() => {
//                 if (!prize) generateRandomPrize();
//               }}
//             >
//               Start Scratching
//             </button>
//           ) : (
//             <div className="space-y-1">
//               {prize?.value && (
//                 <button 
//                   className={`w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white font-semibold ${isDesktop ? 'py-2 px-4 text-lg' : 'py-2 px-3 text-base'} rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200`}
//                 >
//                   Claim Your Reward
//                 </button>
//               )}
//               <button 
//                 onClick={resetGame}
//                 className={`w-full bg-white hover:bg-slate-50 text-slate-700 font-semibold ${isDesktop ? 'py-2 px-4 text-lg' : 'py-1 px-3 text-base'} rounded-xl shadow-lg border border-slate-200 transform hover:scale-105 transition-all duration-200`}
//               >
//                 Try Again
//               </button>
//             </div>
//           )}
//         </div>
        
//         {/* Professional Footer */}
//         <div className="mt-1 text-center">
//           <p className="text-slate-900 text-sm">
//             * Valid for 24 hours. Terms and conditions apply.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// const PhoneMockup = () => {
//   return (
//     <div className="relative group">
//       <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-[3rem] p-3 shadow-2xl border border-slate-600 group-hover:shadow-3xl transition-shadow duration-300">
//         <div className="bg-black rounded-[2.5rem] p-1">
//           <div className="w-[300px] h-[700px] bg-white rounded-[2rem] overflow-hidden relative ">
//             {/* Status Bar */}
//             <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-black/20 to-transparent z-10 flex justify-between items-center px-6 pt-2">
//               <span className="text-white text-sm font-medium">9:41</span>
//               <div className="flex items-center space-x-1">
//                 <div className="w-4 h-2 bg-white rounded-sm opacity-80"></div>
//                 <div className="w-6 h-3 border border-white rounded-sm">
//                   <div className="w-full h-full bg-green-500 rounded-sm"></div>
//                 </div>
//               </div>
//             </div>
            
//             <div className="w-full h-full">
//               <ScratchToWin isDesktop={false} />
//             </div>
//           </div>
//         </div>
        
//         {/* Home Indicator */}
//         <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-slate-500 rounded-full"></div>
//       </div>
//     </div>
//   );
// };

// const DesktopMockup = () => {
//   return (
//     <div className="relative group">
//       <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-6 shadow-2xl border border-slate-600 group-hover:shadow-3xl transition-shadow duration-300">
//         <div className="bg-black rounded-xl p-3">
//           <div className="w-[800px] h-[700px] bg-white rounded-lg overflow-hidden relative">
//             {/* Browser Header */}
//             <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-slate-100 to-slate-50 border-b border-slate-200 flex items-center px-6 z-10">
//               <div className="flex space-x-2">
//                 <div className="w-3 h-3 bg-red-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-green-500 rounded-full"></div>
//               </div>
//               <div className="flex-1 mx-6">
//                 <div className="bg-white rounded-lg px-4 py-2 text-sm text-slate-600 border border-slate-200 shadow-sm">
//                   https://rewards.hm.com/scratch-and-win
//                 </div>
//               </div>
//             </div>
            
//             <div className="w-full h-full pt-12">
//               <ScratchToWin isDesktop={true} />
//             </div>
//           </div>
//         </div>
        
//         {/* Desktop Stand */}
//         <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-6">
//           <div className="w-40 h-8 bg-gradient-to-b from-slate-600 to-slate-800 rounded-b-xl"></div>
//           <div className="w-56 h-4 bg-gradient-to-b from-slate-700 to-slate-900 rounded-full mx-auto shadow-lg"></div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const ResponsiveScratchToWin = () => {
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 p-8">      
//       {/* Mobile View */}
//       <div className="lg:hidden flex items-center justify-center min-h-screen mt-14">
//         <PhoneMockup />
        
//       </div>
      
//       {/* Both Views for Comparison */}
//       <div className="hidden lg:flex lg:flex-row lg:space-x-16 lg:items-center lg:justify-center lg:min-h-screen lg:py-12">
//         <div className="flex flex-col items-center space-y-6">
//           <div className="text-center">
//             <h3 className="text-slate-800 text-2xl font-bold">Desktop Experience</h3>
//             <p className="text-slate-600 text-sm mt-2">Full-featured scratch card interface</p>
//           </div>
//           <DesktopMockup />
//         </div>
        
//         <div className="flex flex-col items-center space-y-6">
//           <div className="text-center">
//             <h3 className="text-slate-800 text-2xl font-bold">Mobile Experience</h3>
//             <p className="text-slate-600 text-sm mt-2">Touch-optimized for mobile devices</p>
//           </div>
//           <PhoneMockup />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ResponsiveScratchToWin;


// import React, { useState, useRef, useEffect } from 'react';

// const ScratchToWin = ({ isDesktop = false }) => {
//   const canvasRef = useRef(null);
//   const [isScratching, setIsScratching] = useState(false);
//   const [scratchPercentage, setScratchPercentage] = useState(0);
//   const [isRevealed, setIsRevealed] = useState(false);
//   const [prize, setPrize] = useState(null);

//   const prizes = [
//     { id: 1, title: "H&M Special Offer", subtitle: "Exclusive Fashion Deals Just for You!", image: "🛍️🧥 H&M" },
//     // { id: 2, title: "FREE Coffee", subtitle: "You won!", image: "☕" },
//     // { id: 3, title: "FREE Burger", subtitle: "Amazing!", image: "🍔" },
//     { id: 2, title: "Try Again", subtitle: "Better luck next time!", image: "🎯" },
//     // { id: 5, title: "FREE Pizza", subtitle: "Fantastic!", image: "🍕" },
//   ];

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     if (!canvas) return;

//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     // Set canvas size
//     canvas.width = rect.width * window.devicePixelRatio;
//     canvas.height = rect.height * window.devicePixelRatio;
//     ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
//     // Create scratch overlay
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#FFD700');
//     gradient.addColorStop(0.5, '#FFA500');
//     gradient.addColorStop(1, '#FF6B35');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add scratch instruction text
//     ctx.fillStyle = '#FFFFFF';
//     ctx.font = `bold ${isDesktop ? '20px' : '16px'} Arial`;
//     ctx.textAlign = 'center';
//     ctx.fillText('Scratch Here!', rect.width / 2, rect.height / 2 - (isDesktop ? 15 : 10));
//     ctx.font = `${isDesktop ? '16px' : '12px'} Arial`;
//     ctx.fillText('🎁 Win Amazing Prizes 🎁', rect.width / 2, rect.height / 2 + (isDesktop ? 20 : 15));
//   }, [isDesktop]);

//   const generateRandomPrize = () => {
//     const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
//     setPrize(randomPrize);
//   };

//   const scratch = (e) => {
//     if (!isScratching) return;
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     let clientX, clientY;
//     if (e.type.includes('touch')) {
//       clientX = e.touches[0].clientX;
//       clientY = e.touches[0].clientY;
//     } else {
//       clientX = e.clientX;
//       clientY = e.clientY;
//     }
    
//     const x = clientX - rect.left;
//     const y = clientY - rect.top;
    
//     ctx.globalCompositeOperation = 'destination-out';
//     ctx.beginPath();
//     ctx.arc(x, y, isDesktop ? 30 : 20, 0, 2 * Math.PI);
//     ctx.fill();
    
//     // Calculate scratch percentage
//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
//     const pixels = imageData.data;
//     let transparent = 0;
    
//     for (let i = 3; i < pixels.length; i += 4) {
//       if (pixels[i] === 0) transparent++;
//     }
    
//     const percentage = (transparent / (pixels.length / 4)) * 100;
//     setScratchPercentage(percentage);
    
//     if (percentage > 40 && !isRevealed) {
//       setIsRevealed(true);
//       if (!prize) generateRandomPrize();
//     }
//   };

//   const startScratch = (e) => {
//     e.preventDefault();
//     setIsScratching(true);
//     if (!prize) generateRandomPrize();
//   };

//   const endScratch = () => {
//     setIsScratching(false);
//   };

//   const resetGame = () => {
//     setIsRevealed(false);
//     setPrize(null);
//     setScratchPercentage(0);
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     ctx.globalCompositeOperation = 'source-over';
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#FFD700');
//     gradient.addColorStop(0.5, '#FFA500');
//     gradient.addColorStop(1, '#FF6B35');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     ctx.fillStyle = '#FFFFFF';
//     ctx.font = `bold ${isDesktop ? '20px' : '16px'} Arial`;
//     ctx.textAlign = 'center';
//     ctx.fillText('Scratch Here!', rect.width / 2, rect.height / 2 - (isDesktop ? 15 : 10));
//     ctx.font = `${isDesktop ? '16px' : '12px'} Arial`;
//     ctx.fillText('🎁 Win Amazing Prizes 🎁', rect.width / 2, rect.height / 2 + (isDesktop ? 20 : 15));
//   };

//   return (
//     <div className="h-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex flex-col items-center justify-center p-2 overflow-y-auto">
//       <div className={`${isDesktop ? 'max-w-lg' : 'max-w-xs'} w-full text-center`}>
//         {/* Background Pattern */}
//         <div className="absolute inset-0 opacity-10 pointer-events-none">
//           <div className={`absolute ${isDesktop ? 'top-20 left-20 text-8xl' : 'top-10 left-10 text-6xl'} animate-bounce`}>🎁</div>
//           <div className={`absolute ${isDesktop ? 'top-40 right-16 text-6xl' : 'top-32 right-8 text-4xl'} animate-pulse`}>⭐</div>
//           <div className={`absolute ${isDesktop ? 'bottom-40 left-12 text-7xl' : 'bottom-32 left-6 text-5xl'} animate-bounce delay-500`}>🎉</div>
//           <div className={`absolute ${isDesktop ? 'bottom-32 right-20 text-5xl' : 'bottom-20 right-12 text-3xl'} animate-pulse delay-300`}>💎</div>
//         </div>
        
//         {/* Header */}
//         <div className={`${isDesktop ? 'mb-6' : 'mb-4'} animate-fadeIn relative z-10`}>
//           {/* <div className={`bg-white/20 backdrop-blur-sm rounded-2xl ${isDesktop ? 'p-6 mb-2' : 'p-4 mb-2'}`}> */}
//             {/* <div className={`${isDesktop ? 'text-4xl mb-3' : 'text-2xl mb-2'}`}>🍕</div> */}
//             {/* <h1 className={`text-white ${isDesktop ? 'text-3xl' : 'text-2xl'} font-bold mb-1`}>Easy Bites</h1> */}
//           {/* </div> */}
          
//           <h2 className={`text-yellow-300 ${isDesktop ? 'text-4xl mt-2' : 'text-3xl mt-4'} font-bold mb-2 animate-pulse `}>Scratch & Win</h2>
//           <h3 className={`text-white ${isDesktop ? 'text-2xl' : 'text-xl'} font-semibold mb-2`}> Exclusive H&M Rewards!</h3>
//           <p className={`text-white/90 ${isDesktop ? 'text-base' : 'text-sm'} ${isDesktop ? 'mb-2' : 'mb-6'}`}>
//           Uncover special fashion deals and surprises just for you! 🎁👗 Scratch the image to reveal your offer at H&M! 🛍️
//           </p>
//         </div>

//         {/* Scratch Card */}
//         <div className={`relative z-10 ${isDesktop ? 'mb-2' : 'mb-4'}`}>
//           <div className={`bg-white rounded-3xl ${isDesktop ? 'p-6' : 'p-6'} shadow-2xl relative overflow-hidden`}>
//             <h3 className={`text-pink-600 ${isDesktop ? 'text-2xl' : 'text-xl'} font-bold text-center mb-2`}>Scratch & Win!</h3>
//             <p className={`text-gray-600 ${isDesktop ? 'text-base' : 'text-sm'} text-center mb-2`}>Find out what's your prize!</p>
            
//             {/* Prize Display (Behind Canvas) */}
//             <div className={`relative w-full ${isDesktop ? 'h-56' : 'h-40'} bg-gradient-to-br from-pink-100 to-yellow-100 rounded-2xl flex items-center justify-center overflow-hidden`}>
//               {prize && (
//                 <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
//                   <div className={`${isDesktop ? 'text-6xl mb-2' : 'text-2xl '}`}>{prize.image}</div>
//                   <h3 className={`${isDesktop ? 'text-3xl' : 'text-2xl'} font-bold text-pink-600 `}>{prize.title}</h3>
//                   <p className={`text-gray-600 ${isDesktop ? 'text-xl' : 'text-md'}`}>{prize.subtitle}</p>
//                   {prize.title !== "Try Again" && (
//                     <div className={`mt-4 bg-pink-500 text-white ${isDesktop ? 'px-8 py-3 text-base' : 'px-3 py-1 text-sm'} rounded-full font-semibold`}>
//                       Claim Now!
//                     </div>
//                   )}
//                 </div>
//               )}
              
//               {/* Scratch Canvas */}
//               <canvas
//                 ref={canvasRef}
//                 className="absolute inset-0 w-full h-full cursor-pointer touch-none"
//                 onMouseDown={startScratch}
//                 onMouseMove={scratch}
//                 onMouseUp={endScratch}
//                 onMouseLeave={endScratch}
//                 onTouchStart={startScratch}
//                 onTouchMove={scratch}
//                 onTouchEnd={endScratch}
//               />
//             </div>
            
//             {/* Progress Indicator */}
//             {scratchPercentage > 0 && !isRevealed && (
//               <div className="mt-4">
//                 <div className={`bg-gray-200 rounded-full ${isDesktop ? 'h-3' : 'h-2'}`}>
//                   <div 
//                     className={`bg-pink-500 ${isDesktop ? 'h-3' : 'h-2'} rounded-full transition-all duration-300`}
//                     style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
//                   />
//                 </div>
//                 <p className={`text-center ${isDesktop ? 'text-sm' : 'text-xs'} text-gray-500 mt-1`}>
//                   {Math.round(scratchPercentage)}% revealed
//                 </p>
//               </div>
//             )}
//           </div>
//         </div>
        
//         {/* Action Buttons */}
//         <div className={`relative z-10 space-y-3 ${isDesktop ? 'mb-4' : 'mb-2'}`}>
//           {!isRevealed ? (
//             <button 
//               className={`w-full bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-bold ${isDesktop ? 'py-3 px-6 text-xl' : 'py-2 px-3 text-lg'} rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200`}
//               onClick={() => {
//                 if (!prize) generateRandomPrize();
//               }}
//             >
//               🎯SCRATCH TO WIN!
//             </button>
//           ) : (
//             <button 
//               onClick={resetGame}
//               className={`w-full bg-green-500 hover:bg-green-400 text-white font-bold ${isDesktop ? 'py-3 px-6 text-xl' : 'py-2 px-3 text-lg'} rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200`}
//             >
//               🎮 Play Again! 🎮
//             </button>
//           )}
          
//           {/* <button className={`w-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold ${isDesktop ? 'py-4 px-8 text-base' : 'py-3 px-6 text-sm'} rounded-2xl border-2 border-white/30 transition-all duration-200`}>
//             📍 Find Store Location
//           </button> */}
//         </div>
        
//         {/* Footer */}
//         <div className="relative z-10 text-center">
//           <p className={`text-white/80 ${isDesktop ? 'text-sm' : 'text-xs'}`}>
//             * Terms and conditions apply. One prize per customer per day.
//           </p>
//         </div>
//       </div>

//       <style jsx>{`
//         @keyframes fadeIn {
//           from { opacity: 0; transform: translateY(20px); }
//           to { opacity: 1; transform: translateY(0); }
//         }
//         .animate-fadeIn {
//           animation: fadeIn 0.6s ease-out;
//         }
//       `}</style>
//     </div>
//   );
// };

// const PhoneMockup = () => {
//   return (
//     <div className="relative">
//       <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-[2.5rem] p-2 shadow-2xl border-4 border-slate-700">
//         <div className="bg-black rounded-[2rem] p-1">
//           <div className="w-[280px] h-[600px] bg-white rounded-[1.5rem] overflow-hidden relative shadow-inner">
//             <div className="absolute top-2 left-4 right-4 flex justify-between items-center z-40 text-white text-xs font-medium">
//               <span>9:41</span>
//               <div className="flex space-x-1">
//                 <div className="w-6 h-3 border border-white rounded-sm">
//                   <div className="w-full h-full bg-green-500 rounded-sm"></div>
//                 </div>
//               </div>
//             </div>
//             <div className="w-full h-full">
//               <ScratchToWin isDesktop={false} />
//             </div>
//           </div>
//         </div>
//         <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-slate-600 rounded-full"></div>
//       </div>
//       <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white to-transparent opacity-10 rounded-[2.5rem] pointer-events-none"></div>
//     </div>
//   );
// };

// const DesktopMockup = () => {
//   return (
//     <div className="relative">
//       <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-4 shadow-2xl border-4 border-slate-700">
//         <div className="bg-black rounded-xl p-2">
//           <div className="w-[800px] h-[700px] bg-white rounded-lg overflow-hidden relative shadow-inner">
//             {/* Browser Header */}
//             <div className="absolute top-0 left-0 right-0 h-10 bg-gray-100 border-b border-gray-300 flex items-center px-4 z-40">
//               <div className="flex space-x-2">
//                 <div className="w-3 h-3 bg-red-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
//                 <div className="w-3 h-3 bg-green-500 rounded-full"></div>
//               </div>
//               <div className="flex-1 mx-4">
//                 <div className="bg-white rounded px-3 py-1 text-xs text-gray-600 border">
//                   https://easybites.com/scratch-and-win
//                 </div>
//               </div>
//             </div>
//             <div className="w-full h-full pt-10">
//               <ScratchToWin isDesktop={true} />
//             </div>
//           </div>
//         </div>
//         {/* Desktop Stand */}
//         <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-4">
//           <div className="w-32 h-6 bg-gradient-to-b from-slate-600 to-slate-800 rounded-b-lg shadow-lg"></div>
//           <div className="w-48 h-3 bg-gradient-to-b from-slate-700 to-slate-900 rounded-full mx-auto shadow-lg"></div>
//         </div>
//       </div>
//       <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white to-transparent opacity-10 rounded-2xl pointer-events-none"></div>
//     </div>
//   );
// };

// const ResponsiveScratchToWin = () => {
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-teal-900 p-8">
//       {/* Desktop View */}
//       {/* <div className="hidden lg:flex items-center justify-center min-h-screen">
//         <DesktopMockup />
//       </div> */}
      
//       {/* Mobile View */}
//       <div className="lg:hidden flex items-center justify-center min-h-screen mt-6">
//         <PhoneMockup />
//       </div>
      
//       {/* Both Views for Comparison (visible on xl screens) */}
//       <div className="hidden xl:flex xl:flex-row xl:space-x-12 xl:items-center xl:justify-center xl:min-h-screen xl:py-12">
//         <div className="flex flex-col items-center">
//           <h3 className="text-white text-xl font-bold mb-4">Desktop Experience</h3>
//           <DesktopMockup />
//         </div>
//         <div className="flex flex-col items-center">
//           <h3 className="text-white text-xl font-bold mb-4">Mobile Experience</h3>
//           <PhoneMockup />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ResponsiveScratchToWin;

// import React, { useState, useRef, useEffect } from 'react';

// const ScratchToWin = () => {
//   const canvasRef = useRef(null);
//   const [isScratching, setIsScratching] = useState(false);
//   const [scratchPercentage, setScratchPercentage] = useState(0);
//   const [isRevealed, setIsRevealed] = useState(false);
//   const [prize, setPrize] = useState(null);

//   const prizes = [
//     { id: 1, title: "FREE Soda", subtitle: "Congratulations!", image: "🥤" },
//     { id: 2, title: "FREE Coffee", subtitle: "You won!", image: "☕" },
//     { id: 3, title: "FREE Burger", subtitle: "Amazing!", image: "🍔" },
//     { id: 4, title: "Try Again", subtitle: "Better luck next time!", image: "🎯" },
//     { id: 5, title: "FREE Pizza", subtitle: "Fantastic!", image: "🍕" },
//   ];

//   useEffect(() => {
//     const canvas = canvasRef.current;
//     if (!canvas) return;

//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     // Set canvas size
//     canvas.width = rect.width * window.devicePixelRatio;
//     canvas.height = rect.height * window.devicePixelRatio;
//     ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
//     // Create scratch overlay
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#FFD700');
//     gradient.addColorStop(0.5, '#FFA500');
//     gradient.addColorStop(1, '#FF6B35');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     // Add scratch instruction text
//     ctx.fillStyle = '#FFFFFF';
//     ctx.font = 'bold 16px Arial';
//     ctx.textAlign = 'center';
//     ctx.fillText('Scratch Here!', rect.width / 2, rect.height / 2 - 10);
//     ctx.font = '12px Arial';
//     ctx.fillText('🎁 Win Amazing Prizes 🎁', rect.width / 2, rect.height / 2 + 15);
//   }, []);

//   const generateRandomPrize = () => {
//     const randomPrize = prizes[Math.floor(Math.random() * prizes.length)];
//     setPrize(randomPrize);
//   };

//   const scratch = (e) => {
//     if (!isScratching) return;
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     let clientX, clientY;
//     if (e.type.includes('touch')) {
//       clientX = e.touches[0].clientX;
//       clientY = e.touches[0].clientY;
//     } else {
//       clientX = e.clientX;
//       clientY = e.clientY;
//     }
    
//     const x = clientX - rect.left;
//     const y = clientY - rect.top;
    
//     ctx.globalCompositeOperation = 'destination-out';
//     ctx.beginPath();
//     ctx.arc(x, y, 20, 0, 2 * Math.PI);
//     ctx.fill();
    
//     // Calculate scratch percentage
//     const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
//     const pixels = imageData.data;
//     let transparent = 0;
    
//     for (let i = 3; i < pixels.length; i += 4) {
//       if (pixels[i] === 0) transparent++;
//     }
    
//     const percentage = (transparent / (pixels.length / 4)) * 100;
//     setScratchPercentage(percentage);
    
//     if (percentage > 40 && !isRevealed) {
//       setIsRevealed(true);
//       if (!prize) generateRandomPrize();
//     }
//   };

//   const startScratch = (e) => {
//     e.preventDefault();
//     setIsScratching(true);
//     if (!prize) generateRandomPrize();
//   };

//   const endScratch = () => {
//     setIsScratching(false);
//   };

//   const resetGame = () => {
//     setIsRevealed(false);
//     setPrize(null);
//     setScratchPercentage(0);
    
//     const canvas = canvasRef.current;
//     const ctx = canvas.getContext('2d');
//     const rect = canvas.getBoundingClientRect();
    
//     ctx.globalCompositeOperation = 'source-over';
//     const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
//     gradient.addColorStop(0, '#FFD700');
//     gradient.addColorStop(0.5, '#FFA500');
//     gradient.addColorStop(1, '#FF6B35');
    
//     ctx.fillStyle = gradient;
//     ctx.fillRect(0, 0, rect.width, rect.height);
    
//     ctx.fillStyle = '#FFFFFF';
//     ctx.font = 'bold 16px Arial';
//     ctx.textAlign = 'center';
//     ctx.fillText('Scratch Here!', rect.width / 2, rect.height / 2 - 10);
//     ctx.font = '12px Arial';
//     ctx.fillText('🎁 Win Amazing Prizes 🎁', rect.width / 2, rect.height / 2 + 15);
//   };

//   return (
//     <div className="mt-16  min-h-screen bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 flex items-center justify-center p-4">
//       {/* Mobile Phone Mockup */}
//       <div className="relative max-w-sm w-full">
//         {/* Phone Frame */}
//         <div className="bg-gray-900 rounded-[3rem] p-2 shadow-2xl">
//           <div className="bg-white rounded-[2.5rem] overflow-hidden">
//             {/* Status Bar */}
//             <div className="bg-gray-900 h-6 flex items-center justify-between px-6 text-white text-xs">
//               <span>9:41</span>
//               <div className="flex items-center space-x-1">
//                 <div className="w-4 h-2 border border-white rounded-sm">
//                   <div className="w-3 h-1 bg-white rounded-sm"></div>
//                 </div>
//               </div>
//             </div>
            
//             {/* App Content */}
//             <div className="bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 min-h-[600px] relative overflow-hidden">
//               {/* Background Pattern */}
//               <div className="absolute inset-0 opacity-10">
//                 <div className="absolute top-10 left-10 text-6xl animate-bounce">🎁</div>
//                 <div className="absolute top-32 right-8 text-4xl animate-pulse">⭐</div>
//                 <div className="absolute bottom-32 left-6 text-5xl animate-bounce delay-500">🎉</div>
//                 <div className="absolute bottom-20 right-12 text-3xl animate-pulse delay-300">💎</div>
//               </div>
              
//               {/* Header */}
//               <div className="relative z-10 text-center pt-8 px-6">
//                 <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 mb-6">
//                   <div className="text-2xl mb-2">🍕</div>
//                   <h1 className="text-white text-2xl font-bold mb-1">Easy Bites</h1>
//                 </div>
                
//                 <h2 className="text-yellow-300 text-3xl font-bold mb-2">Scratch & Win</h2>
//                 <h3 className="text-white text-xl font-semibold mb-4">tasty prizes!</h3>
//                 <p className="text-white/90 text-sm mb-8">
//                   Which tasty treat will be waiting for you at our store? Scratch the image and find out! 🎯
//                 </p>
//               </div>
              
//               {/* Scratch Card */}
//               <div className="relative z-10 px-6 mb-8">
//                 <div className="bg-white rounded-3xl p-6 shadow-2xl relative overflow-hidden">
//                   <h3 className="text-pink-600 text-xl font-bold text-center mb-4">Scratch & Win!</h3>
//                   <p className="text-gray-600 text-sm text-center mb-4">Find out what's your prize!</p>
                  
//                   {/* Prize Display (Behind Canvas) */}
//                   <div className="relative w-full h-48 bg-gradient-to-br from-pink-100 to-yellow-100 rounded-2xl flex items-center justify-center overflow-hidden">
//                     {prize && (
//                       <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
//                         <div className="text-6xl mb-4">{prize.image}</div>
//                         <h3 className="text-2xl font-bold text-pink-600 mb-2">{prize.title}</h3>
//                         <p className="text-gray-600 text-lg">{prize.subtitle}</p>
//                         {prize.title !== "Try Again" && (
//                           <div className="mt-4 bg-pink-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
//                             Claim Now!
//                           </div>
//                         )}
//                       </div>
//                     )}
                    
//                     {/* Scratch Canvas */}
//                     <canvas
//                       ref={canvasRef}
//                       className="absolute inset-0 w-full h-full cursor-pointer touch-none"
//                       onMouseDown={startScratch}
//                       onMouseMove={scratch}
//                       onMouseUp={endScratch}
//                       onMouseLeave={endScratch}
//                       onTouchStart={startScratch}
//                       onTouchMove={scratch}
//                       onTouchEnd={endScratch}
//                     />
//                   </div>
                  
//                   {/* Progress Indicator */}
//                   {scratchPercentage > 0 && !isRevealed && (
//                     <div className="mt-4">
//                       <div className="bg-gray-200 rounded-full h-2">
//                         <div 
//                           className="bg-pink-500 h-2 rounded-full transition-all duration-300"
//                           style={{ width: `${Math.min(scratchPercentage, 100)}%` }}
//                         />
//                       </div>
//                       <p className="text-center text-xs text-gray-500 mt-1">
//                         {Math.round(scratchPercentage)}% revealed
//                       </p>
//                     </div>
//                   )}
//                 </div>
//               </div>
              
//               {/* Action Buttons */}
//               <div className="relative z-10 px-6 space-y-3">
//                 {!isRevealed ? (
//                   <button 
//                     className="w-full bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-bold py-4 px-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200 text-lg"
//                     onClick={() => {
//                       if (!prize) generateRandomPrize();
//                     }}
//                   >
//                     🎯 SCRATCH TO WIN! 🎯
//                   </button>
//                 ) : (
//                   <button 
//                     onClick={resetGame}
//                     className="w-full bg-green-500 hover:bg-green-400 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200 text-lg"
//                   >
//                     🎮 Play Again! 🎮
//                   </button>
//                 )}
                
//                 <button className="w-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold py-3 px-6 rounded-2xl border-2 border-white/30 transition-all duration-200">
//                   📍 Find Store Location
//                 </button>
//               </div>
              
//               {/* Footer */}
//               <div className="relative z-10 text-center pt-6 pb-8 px-6">
//                 <p className="text-white/80 text-xs">
//                   * Terms and conditions apply. One prize per customer per day.
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
        
//         {/* Phone Shadow */}
//         <div className="absolute inset-0 bg-black/20 rounded-[3rem] transform translate-y-4 translate-x-2 -z-10"></div>
//       </div>
//     </div>
//   );
// };

// export default ScratchToWin;

// import React from 'react';
// import { motion } from 'framer-motion';
// import { Zap, Star, ArrowRight, Play } from 'lucide-react';

// const AdPage1= () => {
//   return (
//     <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 overflow-hidden">
//       {/* Floating Elements */}
//       <div className="absolute inset-0">
//         {[...Array(20)].map((_, i) => (
//           <motion.div
//             key={i}
//             className="absolute w-2 h-2 bg-white rounded-full opacity-30"
//             animate={{
//               x: [0, Math.random() * 100 - 50],
//               y: [0, Math.random() * 100 - 50],
//               scale: [1, 1.5, 1],
//               opacity: [0.3, 0.8, 0.3]
//             }}
//             transition={{
//               duration: Math.random() * 3 + 2,
//               repeat: Infinity,
//               delay: Math.random() * 2
//             }}
//             style={{
//               left: `${Math.random() * 100}%`,
//               top: `${Math.random() * 100}%`
//             }}
//           />
//         ))}
//       </div>

//       <div className="relative z-10 flex items-center justify-center min-h-screen px-6">
//         <div className="text-center max-w-4xl">
//           {/* Main Title with Typewriter Effect */}
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ duration: 1 }}
//             className="mb-8"
//           >
//             <motion.h1
//               className="text-8xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 mb-4"
//               animate={{
//                 backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
//               }}
//               transition={{ duration: 3, repeat: Infinity }}
//               style={{ backgroundSize: '200% 200%' }}
//             >
//               NEXUS
//             </motion.h1>
//             <motion.p
//               initial={{ width: 0 }}
//               animate={{ width: '100%' }}
//               transition={{ duration: 2, delay: 0.5 }}
//               className="text-2xl text-white font-light tracking-[0.3em] overflow-hidden whitespace-nowrap border-r-2 border-white mx-auto"
//               style={{ width: 'fit-content' }}
//             >
//               THE FUTURE IS HERE
//             </motion.p>
//           </motion.div>

//           {/* Pulsing CTA Button */}
//           <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1 }}
//             transition={{ delay: 1, type: "spring", stiffness: 200 }}
//             className="mb-12"
//           >
//             <motion.button
//               whileHover={{ scale: 1.1 }}
//               whileTap={{ scale: 0.9 }}
//               animate={{
//                 boxShadow: [
//                   '0 0 20px rgba(236, 72, 153, 0.5)',
//                   '0 0 60px rgba(236, 72, 153, 0.8)',
//                   '0 0 20px rgba(236, 72, 153, 0.5)'
//                 ]
//               }}
//               transition={{ duration: 2, repeat: Infinity }}
//               className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-12 py-6 rounded-full text-xl font-bold flex items-center gap-4 mx-auto shadow-2xl"
//             >
//               <Play className="w-6 h-6" />
//               EXPERIENCE NEXUS
//               <ArrowRight className="w-6 h-6" />
//             </motion.button>
//           </motion.div>

//           {/* Stats Counter Animation */}
//           <motion.div
//             initial={{ opacity: 0, y: 50 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 1.5, duration: 0.8 }}
//             className="grid grid-cols-3 gap-8 max-w-2xl mx-auto"
//           >
//             {[
//               { number: 1000000, suffix: '+', label: 'Users' },
//               { number: 99, suffix: '%', label: 'Satisfaction' },
//               { number: 24, suffix: '/7', label: 'Support' }
//             ].map((stat, index) => (
//               <motion.div
//                 key={index}
//                 className="text-center"
//                 whileHover={{ scale: 1.1 }}
//               >
//                 <motion.div
//                   initial={{ scale: 0 }}
//                   animate={{ scale: 1 }}
//                   transition={{ delay: 2 + index * 0.2, type: "spring" }}
//                   className="text-4xl font-bold text-white mb-2"
//                 >
//                   <motion.span
//                     initial={{ opacity: 0 }}
//                     animate={{ opacity: 1 }}
//                     transition={{ delay: 2.5 + index * 0.2 }}
//                   >
//                     {stat.number.toLocaleString()}{stat.suffix}
//                   </motion.span>
//                 </motion.div>
//                 <p className="text-gray-300">{stat.label}</p>
//               </motion.div>
//             ))}
//           </motion.div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AdPage1;